import { Fragment } from 'react';
import { createPortal } from 'react-dom';
import styles from './ScenarioGenerator.module.css';
import { DEFAULT_GENERATION_MODEL } from '../state/useGenerationState';
import { resolveImageResourceType, resolveImageSizeFromResolution } from './hooks/useCreditCalculations';
import { STORAGE_KEY_GENERATION_MODEL } from './constants';
import { groupGptImageModelsBySection, isGptImageModelCode, stripGptPrefix } from '../utils/imageModelFamily';
import { ModelDropdownSectionHeader } from '../common/ModelDropdownMenu';
import type { GenerationModel } from '../../../features/generation-models/api';
import type { CreditRatesResponse } from '../../../features/organizations/types';
import type { VideoSettings } from './VideoGenerationSettings';
import type { UsePortalDropdownReturn } from './hooks/usePortalDropdown';

interface FrameModelDropdownPortalProps {
  dropdown: UsePortalDropdownReturn;
  scenarioGenerationModel: string;
  setScenarioGenerationModel: (model: string) => void;
  imageGenerationModels: GenerationModel[];
  creditRatesData: CreditRatesResponse | undefined;
  videoSettings: VideoSettings;
}

export function FrameModelDropdownPortal({
  dropdown,
  scenarioGenerationModel,
  setScenarioGenerationModel,
  imageGenerationModels,
  creditRatesData,
  videoSettings,
}: FrameModelDropdownPortalProps) {
  if (!dropdown.openId || !dropdown.pos) return null;

  const activeCode = scenarioGenerationModel || DEFAULT_GENERATION_MODEL;
  const imageSize = resolveImageSizeFromResolution(videoSettings.resolution);
  const lookupCredits = (modelCode: string): number | null => {
    const resourceType = resolveImageResourceType(modelCode, imageSize);
    if (!resourceType || !creditRatesData?.rates) return null;
    const rate = creditRatesData.rates.find(
      (r) => r.service_type === 'image_generation' && r.resource_type === resourceType,
    );
    return rate?.credits_per_unit ?? null;
  };
  // 静止画のモデル選択 (ImageModelFamilySelector) と同じ 3 階層: プロバイダ/バージョン → ファミリー → 品質
  const gptSections = groupGptImageModelsBySection(imageGenerationModels);
  const geminiModels = imageGenerationModels
    .filter((m) => !isGptImageModelCode(m.model_code))
    .sort((a, b) => a.sort_order - b.sort_order);

  // nested=true はファミリー見出し (標準 / 長時間) の配下の行で、見出しに揃えて字下げする
  const renderItem = (modelCode: string, displayLabel: string, nested = false) => {
    const pt = lookupCredits(modelCode);
    const label = pt !== null ? `${displayLabel} (${pt}pt)` : displayLabel;
    return (
      <button
        key={modelCode}
        type="button"
        className={`${styles.modelDropdownItem} ${nested ? styles.modelDropdownItemNested : ''} ${activeCode === modelCode ? styles.modelDropdownItemActive : ''}`}
        onClick={() => {
          setScenarioGenerationModel(modelCode);
          localStorage.setItem(STORAGE_KEY_GENERATION_MODEL, modelCode);
          dropdown.close();
        }}
      >
        {label}
      </button>
    );
  };

  return createPortal(
    <div
      ref={dropdown.menuRef}
      className={styles.modelDropdownMenu}
      style={{ position: 'fixed', top: dropdown.pos.top, left: dropdown.pos.left }}
    >
      {gptSections.map((section, idx) => (
        <Fragment key={section.key}>
          {idx > 0 && <div className={styles.modelDropdownSeparator} />}
          <ModelDropdownSectionHeader label={section.label} iconMask="/openai_icon.svg" />
          {section.families.map((family) => (
            <Fragment key={family.key}>
              <ModelDropdownSectionHeader label={family.label} tooltip={family.tooltip} sub />
              {family.list.map((m) => renderItem(m.model_code, stripGptPrefix(m.display_label), true))}
            </Fragment>
          ))}
        </Fragment>
      ))}
      {gptSections.length > 0 && geminiModels.length > 0 && <div className={styles.modelDropdownSeparator} />}
      {geminiModels.length > 0 && (
        <>
          <ModelDropdownSectionHeader label="Google Gemini" icon="🍌" />
          {geminiModels.map((m) => renderItem(m.model_code, m.display_label))}
        </>
      )}
    </div>,
    document.body,
  );
}
